import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import DataTable from "../../components/DataGrids/DataGrid";
import { useToast } from "../../services/utils";
import FlexBetween from "../../components/FlexBetween";
import ButtonComponent from "../../components/Button.Component";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { paginatedResponseFormat } from "../../services/constants";
import {
  deleteResourceColumn,
  reactivateResourceColumn,
} from "../../services/resourcesService";
import useFetchPaginatedResourceColumnsByDomain from "../../hooks/useFetchPaginatedResourceColumnsByDomain";
import { useResourceContext } from "../../contexts/ResourceContext";
import {
  downloadResourceColumnExportFile,
  getFormattedDateTime,
} from "../../services/utils";
import {
  IconAudit,
  IconDeleteBlueSvg,
  IconExportIconBlue,
  IconEyeBase,
  IconEditBase,
  IconReactivateSvg,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
} from "../../common/utils/icons";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import Loader from "../../components/Molecules/Loader/Loader";
import AssociateEntitiesDialog from "../../components/Dialogs/AssociateEntitiesDialog";
import useFetchDomains from "../../hooks/useFetchDomains";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";

// Centralized Filter Configuration for Resource Columns
const RESOURCE_COLUMN_FILTER_CONFIG = {
  DEFAULT_STATE: {
    domain_id: "",
    query_name: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  FIELD_TYPES: {
    ID_FIELDS: ["domain_id"],
    STRING_FIELDS: ["query_name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const ResourceColumns: React.FC = () => {
  const { domainsData, setDomainsData } = useResourceContext();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  const canAddResourceColumn = hasPermission("resource_column_details", "add");
  const canViewResourceColumn = hasPermission(
    "resource_column_details",
    "view"
  );
  const canDeleteResourceColumn = hasPermission(
    "resource_column_details",
    "delete"
  );
  const canEditResourceColumn = hasPermission(
    "resource_column_details",
    "edit"
  );

  //for pagination default values and states
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  //for navigation & parameters
  const navigate = useNavigate();
  const { domainId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [fileData, setFileData] = useState<any>(paginatedResponseFormat);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDomainsLoading, setDomainsIsLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [openAssociateEntitiesDialog, setOpenAssociateEntitiesDialog] =
    useState<boolean>(false);
  const [associateEntityId, setAssociateEntityId] = useState<number | null>(
    null
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [resourceColumnToDelete, setResourceColumnToDelete] =
    useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [domains] = useFetchDomains({ setIsLoading: setDomainsIsLoading });

  // SearchFilterBar state management - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...RESOURCE_COLUMN_FILTER_CONFIG.DEFAULT_STATE,
      domain_id: Number(domainId) || "", // Domain from route parameter takes precedence
    };

    // Restore filters from URL parameters (but don't override domain_id from route)
    RESOURCE_COLUMN_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      if (field === "domain_id") return; // Skip domain_id, use route parameter instead
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  // Extract filters from searchFilterData
  const effectiveDomainId = searchFilterData?.domain_id || "";
  const effectiveSearchTerm = searchFilterData?.query_name || "";
  const effectiveStatusFilter = searchFilterData?.status_filter || "Active";

  // Map status filter to isActive parameter
  const isActive =
    effectiveStatusFilter === "Active"
      ? "true"
      : effectiveStatusFilter === "Inactive"
      ? "false"
      : "all";

  const [fetchedResourcesColumnData, refetchResourceColumns] =
    useFetchPaginatedResourceColumnsByDomain({
      currentDomainId: effectiveDomainId,
      setIsLoading,
      page,
      pSize,
      searchTerm: effectiveSearchTerm,
      isActive,
    }) as [any, () => Promise<void>];

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    RESOURCE_COLUMN_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      if (field === "domain_id") return; // Skip domain_id, it's in the route parameter
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...RESOURCE_COLUMN_FILTER_CONFIG.DEFAULT_STATE,
      domain_id: Number(domainId) || "", // Always use domain from route
    };

    // Extract filter parameters from URL (except domain_id)
    RESOURCE_COLUMN_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      if (field === "domain_id") return; // Skip domain_id, use route parameter
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? ""
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? ""
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes to prevent infinite loops
    if (hasFilterChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams, domainId]); // Depend on searchParams and domainId

  // Reset page to 1 when search filter or status filter changes
  useEffect(() => {
    setPage(1);
  }, [searchFilterData]);

  useEffect(() => {
    if (domains) {
      setDomainsData(domains);
    }
  }, [domains]);

  // Handle reactivate resource column
  const handleReactivate = async (id: number, resourceColumnName: string) => {
    setIsLoading(true);
    try {
      await reactivateResourceColumn(id);
      showToast(
        `Resource Column "${resourceColumnName}" reactivated successfully`,
        "success"
      );

      // Optimistic UI update - immediately update the is_active flag in local state
      setFileData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === id ? { ...item, is_active: true } : item
        ),
      }));

      // Refetch data in background for consistency
      refetchResourceColumns();
    } catch (error) {
      showToast("Error reactivating resource column", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handel events

  const handelClickEvent = (url: any) => {
    navigate(url);
  };
  const handleAssociateEntitiesDialog = () => {
    setOpenAssociateEntitiesDialog(true);
  };

  // Handle delete resource column
  const handleDeleteResourceColumn = (resourceColumn: any) => {
    setResourceColumnToDelete(resourceColumn);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!resourceColumnToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteResourceColumn(resourceColumnToDelete.id);
      if (result && result.status === 200) {
        showToast(
          `${resourceColumnToDelete.name} deleted successfully`,
          "success"
        );
        setDeleteConfirmOpen(false);
        setResourceColumnToDelete(null);
        // Update the resource columns list to remove the deleted resource column
        setFileData((prev: any) => ({
          ...prev,
          items: prev?.items?.filter(
            (prevItem: any) => prevItem.id !== resourceColumnToDelete.id
          ),
        }));
      }
    } catch (error: any) {
      showToast(error?.message || "Failed to delete resource column", "error");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Table Columns
  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: "ID",
      minWidth: 60,
      renderCell: (params: any) => {
        return <span>{params.id}</span>;
      },
    },
    {
      field: "name",
      headerName: "Resource Column Name",
      width: 120,
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "domain_id",
      headerName: "Domain Name",
      flex: 1,
      renderCell: (params: any) => {
        const dataTypeValue = domainsData.find(
          (item: { id: any }) => item.id === params.row.domain_id
        )?.domain_name;

        return <LimitChractersWithTooltip value={dataTypeValue} />;
      },
    },
    {
      field: "modified_by_user",
      headerName: "Modified By",
      minWidth: 140,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value?.username} />;
      },
    },
    {
      field: "updated_on",
      headerName: "Last Modified",
      minWidth: 180,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      minWidth: 80,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        const isActive = params.row.is_active === true;
        return (
          <Tooltip
            title={isActive ? "Active" : "Inactive"}
            placement="top"
            arrow
          >
            <span>
              {isActive ? (
                <IconCheckCircleIconGreenSvg />
              ) : (
                <IconCrossCircleIconSvg />
              )}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      width: 140,
      filterable: false,
      renderCell: (params) => {
        const isActive = params.row.is_active === true;
        const handleDelete = () => {
          handleDeleteResourceColumn(params.row);
        };
        return (
          <>
            {/* For active records, show all action buttons */}
            {isActive && (
              <>
                {canViewResourceColumn && (
                  <Tooltip title="View Resource Column" placement="top" arrow>
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(
                          `/resource-columns/${domainId}/view/${params.row.id}`
                        )
                      }
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canEditResourceColumn && (
                  <Tooltip title="Edit Resource Column" placement="top" arrow>
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(
                          `/resource-columns/${domainId}/edit/${params.row.id}`
                        )
                      }
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                <DropdownMenu
                  dropdownMenuItems={[
                    canViewResourceColumn && (
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() =>
                          navigate(
                            `/resource-columns/${domainId}/auditResourceColumn/${params.row.id}`
                          )
                        }
                      >
                        <IconAudit /> Audit Resource Column
                      </IconButton>
                    ),
                    canViewResourceColumn && (
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() => {
                          setAssociateEntityId(params.row.id);
                          handleAssociateEntitiesDialog();
                        }}
                      >
                        <IconExportIconBlue />
                        Export
                      </IconButton>
                    ),
                    canDeleteResourceColumn && (
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() => handleDelete()}
                      >
                        <IconDeleteBlueSvg />
                        Delete Resource Column
                      </IconButton>
                    ),
                  ]}
                />
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isActive && (
              <Tooltip title="Reactivate Resource Column" placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    handleReactivate(params.row.id, params.row.name)
                  }
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  // for update resource list data
  useEffect(() => {
    if (fetchedResourcesColumnData) {
      setFileData(fetchedResourcesColumnData);
    }
  }, [fetchedResourcesColumnData]);

  return (
    <Box>
      {(isDownloadLoading || isDomainsLoading || isLoading) && (
        <Loader
          isLoading={isDownloadLoading || isDomainsLoading || isLoading}
        />
      )}
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete Resource Column"
        dailogDescription={`Are you sure you want to delete resource column "${resourceColumnToDelete?.name}"?`}
      />
      {/* Consolidated Filters - Domain, Search, and Status */}
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={["domain_list", "query_name", "status_filter"]}
              primaryFields={["domain_list", "query_name", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active",
                includeDomainId: false,
              }}
            />
          </Grid>
          <Grid
            item
            xs="auto"
            sx={{ display: "flex", alignItems: "flex-start" }}
          >
            {canAddResourceColumn && (
              <ButtonComponent
                className="btn-orange"
                handelClickEvent={() =>
                  handelClickEvent("/resource-columns/add-columns")
                }
              >
                <AddSharpIcon sx={{ marginRight: "4px" }} /> Resource Column
                Details
              </ButtonComponent>
            )}
          </Grid>
        </Grid>
      </Box>
      {/* import data table for resource list  */}

      <FlexBetween>
        <DataTable
          dataRows={fileData?.items || []}
          dataColumns={columns || []}
          loading={isLoading}
          dataListTitle="Resource Columns List"
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total || 0}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={370}
        />
      </FlexBetween>
      <AssociateEntitiesDialog
        openAssociateEntitiesDialog={openAssociateEntitiesDialog}
        setOpenAssociateEntitiesDialog={setOpenAssociateEntitiesDialog}
        associateEntityId={associateEntityId}
        downloadExportFile={downloadResourceColumnExportFile}
        setIsDownloadLoading={setIsDownloadLoading}
        header={"Resource Column"}
      />
    </Box>
  );
};

export default ResourceColumns;
